import { GrowthBook } from '@growthbook/growthbook-react'
import { createPromiseFromSignal, library } from '@susu/analytics-client'
import { isConsentCategoryEnabled, OneTrustCategories } from '@susu/cookie'

import {
  type Environment,
  GROWTHBOOK_API_HOST,
  GROWTHBOOK_CLIENT_KEY,
} from '../../config'
import { avoEvent } from '../analytics'

const clients = new Map<Environment, GrowthBook>()
const loaders = new Map<Environment, Promise<void>>()

/**
 * The GrowthBook client for an environment — one per page, not one per mount.
 *
 * Per-mount clients were the obvious shape and are the wrong one here:
 * `destroy()` in an unmount cleanup cannot be undone — a destroyed client stays
 * destroyed, which both StrictMode's dev remount and a host that reopens its
 * panel need it not to be — and without a cleanup each open would leak a
 * client. Sharing one also means the payload is already in memory on the second
 * open, so a screen gated on a flag resolves without a loader.
 *
 * No plugins. Experiments bucket on the `id` attribute — GrowthBook's default
 * hash attribute — which `FeatureFlagsProvider` fills with Segment's
 * `anonymousId` (see `getExperimentId`). `autoAttributesPlugin` would fill it
 * with its own `gbuuid` cookie instead, an id the warehouse cannot join to a
 * Segment user. `thirdPartyTrackingPlugin` is skipped for the same control:
 * the callback below sends the exposure through Avo, typed and with the
 * standard booking properties, rather than a bare `analytics.track`.
 */
export function getGrowthBook(env: Environment): GrowthBook {
  const existing = clients.get(env)
  if (existing) return existing

  const client = new GrowthBook({
    apiHost: GROWTHBOOK_API_HOST,
    clientKey: GROWTHBOOK_CLIENT_KEY[env],
    // Exposes `window._growthbook` for the GrowthBook devtools extension,
    // which is how you force this flag locally.
    enableDevMode: env !== 'prd',
    // Fires once per experiment and variation for this client, and only for a
    // visitor actually bucketed — never without an `id`, so never without
    // analytics consent. `applicationId` rides along as an Avo system property.
    trackingCallback: (experiment, result) => {
      avoEvent('experimentViewed', {
        experimentId: experiment.key,
        variationId: result.key,
      })
    },
  })

  clients.set(env, client)
  return client
}

/**
 * Loads the feature payload, once per environment.
 *
 * Resolves when the load settles rather than when it succeeds, and never
 * rejects: a blocked CDN, an ad blocker or an offline dev machine has to leave
 * every flag on its default instead of parking a gated screen on its loader.
 *
 * Not streaming — one fetch per page. A flag flipped mid-session lands on the
 * visitor's next page load, which for a booking widget is soon enough and
 * costs no long-lived connection inside a host page.
 */
export function loadFeatures(env: Environment): Promise<void> {
  const pending = loaders.get(env)
  if (pending) return pending

  const loading = getGrowthBook(env)
    .init({ streaming: false })
    .then(() => undefined)
    .catch(() => undefined)

  loaders.set(env, loading)
  return loading
}

/** How long an experiment read waits for Segment before giving up on the id. */
const SEGMENT_TIMEOUT_MS = 3000

/**
 * The id experiments bucket on: Segment's `anonymousId`, the key GrowthBook's
 * `anonymous_id` assignment query joins exposures on in the warehouse.
 *
 * `null` without analytics consent (OneTrust `C0002`). An exposure we may not
 * report cannot be analysed, so the visitor stays out of every experiment and
 * falls through to the next rule — GrowthBook skips an experiment whose hash
 * attribute is empty. Consent is read first so that case answers at once.
 *
 * With consent, waits for Segment, but at most `SEGMENT_TIMEOUT_MS`: a screen
 * gated on an experiment waits for this answer, so a blocked script must not
 * hold it. A timeout reads as `null` for the rest of the page. Never rejects.
 */
export async function getExperimentId(): Promise<string | null> {
  if (!isConsentCategoryEnabled(OneTrustCategories.ANALYTICS)) return null

  // `library` holds the AnalyticsBrowser, a thenable: awaiting it waits for
  // Segment to load and yields the loaded `Analytics`.
  const loaded = (async () => {
    const [segment] = await createPromiseFromSignal(library)
    return segment.user().anonymousId() ?? null
  })()
  const timeout = new Promise<null>((resolve) =>
    setTimeout(() => resolve(null), SEGMENT_TIMEOUT_MS),
  )

  try {
    return await Promise.race([loaded, timeout])
  } catch {
    return null
  }
}
